import type { Root } from "../../domain/app-types.ts";

export interface SpaceManagementCapabilities {
  roots(): Root[];
  setRoots(roots: Root[]): void;
  activeRoot(): string | null;
  setActiveRoot(id: string | null): void;
  clearCategory(): void;
  clearSelectedTask(): void;
  promptCreate(): Promise<string | null>;
  promptRename(root: Root): Promise<string | null>;
  promptTransfer(root: Root): Promise<string | null>;
  confirmDelete(root: Root): Promise<boolean>;
  confirmTransfer(email: string): Promise<boolean>;
  create(name: string): Promise<Root | null>;
  rename(root: Root, name: string): Promise<Root | null>;
  delete(root: Root): Promise<boolean>;
  archive(root: Root, archive: boolean): Promise<boolean>;
  transfer(root: Root, email: string): Promise<string | null>;
  persistActiveRoot(id: string | null): Promise<void>;
  refreshAccess(): Promise<void>;
  render(): Promise<void>;
  toast(message: string, error?: boolean): void;
  nowIso(): string;
}

export function createSpaceManagementController(capabilities: SpaceManagementCapabilities) {
  return {
    async add(): Promise<void> {
      const name = await capabilities.promptCreate(); if (!name) return;
      const root = await capabilities.create(name);
      if (!root) { capabilities.toast("Connect to the server to create a space", true); return; }
      capabilities.setRoots([...capabilities.roots(), root]); capabilities.setActiveRoot(root.id); await capabilities.persistActiveRoot(root.id); await capabilities.render();
    },
    async rename(id: string): Promise<void> {
      const root = capabilities.roots().find(item => item.id === id); if (!root) return;
      const name = await capabilities.promptRename(root); if (!name || name === root.name) return;
      const updated = await capabilities.rename(root, name);
      if (!updated) { capabilities.toast("Could not rename category", true); return; }
      Object.assign(root, updated); await capabilities.render(); capabilities.toast("Category renamed");
    },
    async delete(id: string): Promise<void> {
      const root = capabilities.roots().find(item => item.id === id); if (!root || !await capabilities.confirmDelete(root)) return;
      if (!await capabilities.delete(root)) { capabilities.toast("Could not delete category", true); return; }
      const remaining = capabilities.roots().filter(item => item.id !== id); capabilities.setRoots(remaining);
      if (capabilities.activeRoot() === id) { const next = remaining[0]?.id ?? null; capabilities.setActiveRoot(next); capabilities.clearCategory(); await capabilities.persistActiveRoot(next); }
      capabilities.clearSelectedTask(); await capabilities.render(); capabilities.toast("Category deleted");
    },
    async archive(id: string): Promise<void> {
      const root = capabilities.roots().find(item => item.id === id); if (!root) return;
      const archiving = !root.archived_at;
      if (!await capabilities.archive(root, archiving)) { capabilities.toast("Could not update archive", true); return; }
      root.archived_at = archiving ? capabilities.nowIso() : null;
      if (capabilities.activeRoot() === id && archiving) { const next = capabilities.roots().find(item => !item.archived_at)?.id || null; capabilities.setActiveRoot(next); capabilities.clearCategory(); await capabilities.persistActiveRoot(next); }
      await capabilities.render(); capabilities.toast(archiving ? "Space archived" : "Space restored");
    },
    async transfer(id: string): Promise<void> {
      const root = capabilities.roots().find(item => item.id === id); if (!root || root.role !== "owner") return;
      const email = await capabilities.promptTransfer(root); if (!email || !await capabilities.confirmTransfer(email)) return;
      const error = await capabilities.transfer(root, email);
      if (error) { capabilities.toast(error, true); return; }
      root.role = "editor"; await capabilities.refreshAccess(); await capabilities.render(); capabilities.toast("Space ownership transferred");
    },
  };
}
